import React, { useState, useEffect, useCallback } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Switch,
    Space,
    Typography,
    Tag,
    Tooltip,
    message,
    Grid,
    Card,
    Divider,
    Empty,
    Spin,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    ReloadOutlined,
    UserOutlined,
    PhoneOutlined,
    InfoCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

const getStoredToken = () => {
    const keys = ["token", "accessToken", "AuthToken", "jwtToken"];
    for (const k of keys) {
        const v = localStorage.getItem(k);
        if (v) return v.replace(/^"|"$/g, "");
    }
    try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return user?.token || user?.accessToken || null;
    } catch {
        return null;
    }
};

const getAuthHeaders = () => {
    const token = getStoredToken();
    return token
        ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
        : { "Content-Type": "application/json" };
};

const getUserID = () => localStorage.getItem("userName") || "";

const ManageSupplier = () => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [form] = Form.useForm();

    const baseUrl = global.config?.APIURL?.BaseURL || "";

    // ─── Fetch Suppliers ──────────────────────────────────────────────────────
    const fetchSuppliers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${baseUrl}api/Supplier/GetListOfSuppliers`,
                { headers: getAuthHeaders() }
            );
            const data = Array.isArray(res.data) ? res.data : [];
            setSuppliers(data);
        } catch (err) {
            console.error("Error fetching suppliers:", err);
            message.error("Failed to load suppliers.");
        } finally {
            setLoading(false);
        }
    }, [baseUrl]);

    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    // ─── Open Modal ───────────────────────────────────────────────────────────
    const openAddModal = () => {
        setEditingSupplier(null);
        form.resetFields();
        form.setFieldsValue({ IsActive: true, UserID: getUserID() });
        setModalVisible(true);
    };

    const openEditModal = (supplier) => {
        setEditingSupplier(supplier);
        form.setFieldsValue({
            SupplierID: supplier.SupplierID,
            SupName: supplier.SupName,
            SupplierIDonLocal: supplier.SupplierIDonLocal,
            UserID: supplier.UserID || getUserID(),
            SupContactNo: supplier.SupContactNo,
            IsActive: supplier.IsActive,
            Detail: supplier.Detail,
        });
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setEditingSupplier(null);
        form.resetFields();
    };

    // ─── Submit (Create / Update) ─────────────────────────────────────────────
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);

            const payload = {
                SupplierID: editingSupplier ? editingSupplier.SupplierID : 0,
                SupName: values.SupName,
                SupplierIDonLocal: values.SupplierIDonLocal
                    ? Number(values.SupplierIDonLocal)
                    : 0,
                UserID: values.UserID || getUserID(),
                SupContactNo: values.SupContactNo || "",
                IsActive: values.IsActive ?? true,
                Detail: values.Detail || "",
            };

            await axios.post(
                `${baseUrl}api/Supplier/CreateSupplier`,
                payload,
                { headers: getAuthHeaders() }
            );

            message.success(
                editingSupplier
                    ? "Supplier updated successfully!"
                    : "Supplier created successfully!"
            );
            closeModal();
            fetchSuppliers();
        } catch (err) {
            if (err?.errorFields) return; // Ant Design validation error
            console.error("Error saving supplier:", err);
            message.error("Failed to save supplier. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Table Columns ────────────────────────────────────────────────────────
    const columns = [
        {
            title: "ID",
            dataIndex: "SupplierID",
            key: "SupplierID",
            width: 70,
            sorter: (a, b) => a.SupplierID - b.SupplierID,
        },
        {
            title: "Supplier Name",
            dataIndex: "SupName",
            key: "SupName",
            sorter: (a, b) => (a.SupName || "").localeCompare(b.SupName || ""),
            render: (text) => (
                <Text strong style={{ color: "#1890ff" }}>
                    {text}
                </Text>
            ),
        },
        {
            title: "Contact No.",
            dataIndex: "SupContactNo",
            key: "SupContactNo",
            render: (val) =>
                val ? (
                    <Space>
                        <PhoneOutlined style={{ color: "#52c41a" }} />
                        {val}
                    </Space>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: "Detail",
            dataIndex: "Detail",
            key: "Detail",
            ellipsis: true,
            render: (val) =>
                val ? (
                    <Tooltip title={val}>
                        <Text ellipsis style={{ maxWidth: 200 }}>
                            {val}
                        </Text>
                    </Tooltip>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: "Status",
            dataIndex: "IsActive",
            key: "IsActive",
            width: 100,
            filters: [
                { text: "Active", value: true },
                { text: "Inactive", value: false },
            ],
            onFilter: (value, record) => record.IsActive === value,
            render: (val) =>
                val ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">
                        Active
                    </Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined />} color="error">
                        Inactive
                    </Tag>
                ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 100,
            render: (_, record) => (
                <Tooltip title="Edit Supplier">
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => openEditModal(record)}
                        style={{ borderRadius: 6 }}
                    >
                        Edit
                    </Button>
                </Tooltip>
            ),
        },
    ];

    // ─── Mobile Card View ─────────────────────────────────────────────────────
    const MobileCard = ({ supplier }) => (
        <Card
            hoverable
            style={{
                borderRadius: 12,
                boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                marginBottom: 12,
                border: "1px solid #f0f0f0",
                background: supplier.IsActive ? "#fff" : "#fafafa",
            }}
            bodyStyle={{ padding: "14px 16px" }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 10,
                }}
            >
                <div>
                    <Text
                        strong
                        style={{ fontSize: 15, color: "#1890ff", display: "block" }}
                    >
                        {supplier.SupName}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        ID: {supplier.SupplierID}
                    </Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {supplier.IsActive ? (
                        <Tag icon={<CheckCircleOutlined />} color="success">
                            Active
                        </Tag>
                    ) : (
                        <Tag icon={<CloseCircleOutlined />} color="error">
                            Inactive
                        </Tag>
                    )}
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => openEditModal(supplier)}
                        style={{ borderRadius: 6 }}
                    />
                </div>
            </div>

            {supplier.SupContactNo && (
                <div
                    style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}
                >
                    <PhoneOutlined style={{ color: "#52c41a", fontSize: 13 }} />
                    <Text style={{ fontSize: 13 }}>{supplier.SupContactNo}</Text>
                </div>
            )}
            {supplier.Detail && (
                <div
                    style={{ display: "flex", alignItems: "flex-start", gap: 6 }}
                >
                    <InfoCircleOutlined
                        style={{ color: "#faad14", fontSize: 13, marginTop: 2 }}
                    />
                    <Text style={{ fontSize: 13, color: "#555" }}>{supplier.Detail}</Text>
                </div>
            )}
        </Card>
    );

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div
            style={{
                padding: isMobile ? "12px" : "24px",
                background: "#f5f6fa",
                minHeight: "100vh",
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 20,
                    flexWrap: "wrap",
                    gap: 12,
                }}
            >
                <Title
                    level={isMobile ? 4 : 3}
                    style={{ margin: 0, color: "#1890ff" }}
                >
                    <UserOutlined style={{ marginRight: 8 }} />
                    Manage Suppliers
                </Title>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchSuppliers}
                        loading={loading}
                        style={{ borderRadius: 6 }}
                    >
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openAddModal}
                        style={{
                            borderRadius: 6,
                            background: "#1890ff",
                        }}
                    >
                        Add Supplier
                    </Button>
                </Space>
            </div>

            {/* Summary Bar */}
            <div
                style={{
                    display: "flex",
                    gap: 12,
                    marginBottom: 20,
                    flexWrap: "wrap",
                }}
            >
                <Card
                    size="small"
                    style={{
                        borderRadius: 8,
                        background: "#e6f7ff",
                        border: "1px solid #91d5ff",
                        minWidth: 120,
                    }}
                    bodyStyle={{ padding: "8px 16px" }}
                >
                    <Text style={{ color: "#1890ff", fontWeight: 600 }}>
                        Total: {suppliers.length}
                    </Text>
                </Card>
                <Card
                    size="small"
                    style={{
                        borderRadius: 8,
                        background: "#f6ffed",
                        border: "1px solid #b7eb8f",
                        minWidth: 120,
                    }}
                    bodyStyle={{ padding: "8px 16px" }}
                >
                    <Text style={{ color: "#52c41a", fontWeight: 600 }}>
                        Active: {suppliers.filter((s) => s.IsActive).length}
                    </Text>
                </Card>
                <Card
                    size="small"
                    style={{
                        borderRadius: 8,
                        background: "#fff1f0",
                        border: "1px solid #ffa39e",
                        minWidth: 120,
                    }}
                    bodyStyle={{ padding: "8px 16px" }}
                >
                    <Text style={{ color: "#ff4d4f", fontWeight: 600 }}>
                        Inactive: {suppliers.filter((s) => !s.IsActive).length}
                    </Text>
                </Card>
            </div>

            {/* Content */}
            {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                    <Spin size="large" />
                </div>
            ) : suppliers.length === 0 ? (
                <Card
                    style={{ borderRadius: 12, textAlign: "center", padding: "40px 0" }}
                >
                    <Empty
                        description="No suppliers found"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openAddModal}
                        style={{ marginTop: 16, borderRadius: 6 }}
                    >
                        Add First Supplier
                    </Button>
                </Card>
            ) : isMobile ? (
                <div>
                    {suppliers.map((s) => (
                        <MobileCard key={s.SupplierID} supplier={s} />
                    ))}
                </div>
            ) : (
                <Card
                    style={{
                        borderRadius: 12,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                        border: "1px solid #f0f0f0",
                    }}
                    bodyStyle={{ padding: 0 }}
                >
                    <Table
                        dataSource={suppliers}
                        columns={columns}
                        rowKey="SupplierID"
                        pagination={{
                            pageSize: 20,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50", "100"],
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} suppliers`,
                        }}
                        bordered={false}
                        size="middle"
                        rowClassName={(record) =>
                            !record.IsActive ? "supplier-row-inactive" : ""
                        }
                        style={{ borderRadius: 12 }}
                    />
                </Card>
            )}

            {/* Add / Edit Modal */}
            <Modal
                title={
                    <Space>
                        {editingSupplier ? (
                            <EditOutlined style={{ color: "#1890ff" }} />
                        ) : (
                            <PlusOutlined style={{ color: "#52c41a" }} />
                        )}
                        <span>
                            {editingSupplier
                                ? `Edit Supplier — ${editingSupplier.SupName}`
                                : "Add New Supplier"}
                        </span>
                    </Space>
                }
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={closeModal}
                confirmLoading={submitting}
                okText={editingSupplier ? "Update" : "Create"}
                cancelText="Cancel"
                width={isMobile ? "95%" : 520}
                destroyOnClose
                okButtonProps={{
                    style: {
                        borderRadius: 6,
                        background: editingSupplier ? "#1890ff" : "#52c41a",
                        borderColor: editingSupplier ? "#1890ff" : "#52c41a",
                    },
                }}
                cancelButtonProps={{ style: { borderRadius: 6 } }}
            >
                <Divider style={{ margin: "12px 0" }} />
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ IsActive: true, UserID: getUserID() }}
                >
                    {/* Hidden SupplierID when editing */}
                    <Form.Item name="SupplierID" hidden>
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Supplier Name"
                        name="SupName"
                        rules={[
                            { required: true, message: "Please enter the supplier name" },
                            { min: 2, message: "Name must be at least 2 characters" },
                        ]}
                    >
                        <Input
                            placeholder="e.g. ABC Jewels Pvt Ltd"
                            prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                            maxLength={200}
                            showCount
                        />
                    </Form.Item>

                    <Form.Item
                        label="Contact Number"
                        name="SupContactNo"
                        rules={[
                            {
                                pattern: /^[0-9+\-\s()]*$/,
                                message: "Enter a valid contact number",
                            },
                        ]}
                    >
                        <Input
                            placeholder="e.g. 9876543210"
                            prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                            maxLength={20}
                        />
                    </Form.Item>

                    <Form.Item
                        label="User ID"
                        name="UserID"
                        rules={[
                            { required: true, message: "User ID is required" },
                        ]}
                    >
                        <Input placeholder="User ID" maxLength={100} />
                    </Form.Item>

                    <Form.Item label="Local Supplier ID" name="SupplierIDonLocal">
                        <Input
                            type="number"
                            placeholder="e.g. 101 (optional)"
                            style={{ width: "100%" }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Detail / Notes"
                        name="Detail"
                    >
                        <TextArea
                            rows={3}
                            placeholder="Add any additional notes or details about the supplier..."
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>

                    <Form.Item
                        label="Active Status"
                        name="IsActive"
                        valuePropName="checked"
                    >
                        <Switch
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Inline style for inactive rows */}
            <style>{`
                .supplier-row-inactive {
                    background: #fafafa !important;
                    opacity: 0.75;
                }
                .supplier-row-inactive:hover > td {
                    background: #f5f5f5 !important;
                }
            `}</style>
        </div>
    );
};

export default ManageSupplier;
